import { useEffect, useState } from "react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import MobileCTA from "@/components/site/MobileCTA";
import ScrollScanner from "@/components/ScrollScanner";
import Breadcrumbs from "@/components/site/Breadcrumbs";
import SectionHeader from "@/components/site/SectionHeader";
import Reveal from "@/components/site/Reveal";
import { base44 } from "@/api/base44Client";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { useJsonLd } from "@/lib/useJsonLd";

const DOMAIN = "https://meridianbedbug.ca";

export default function BedBugGuideIndex() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: "Bed Bug Guide — Practical Answers for Vancouver | Meridian",
    description:
      "The Meridian Bed Bug Guide: practical, search-focused articles on bed bug behaviour, signs, spread, prevention and treatment across Vancouver and the Lower Mainland.",
    path: "/guide",
  });

  useEffect(() => {
    let active = true;
    const today = new Date().toISOString().slice(0, 10);
    base44.entities.BlogPost.list("-published_date", 500)
      .then((rows) => {
        if (!active) return;
        const visible = (rows || [])
          .filter((p) => p.status === "published")
          .filter((p) => !p.published_date || p.published_date <= today);
        setPosts(visible);
        setLoading(false);
      })
      .catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useJsonLd("guide-index-jsonld", {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "The Meridian Bed Bug Guide",
    itemListElement: posts.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: p.title,
      url: `${DOMAIN}/guide/${p.slug}`,
    })),
  });

  return (
    <div className="overflow-x-hidden pb-16 lg:pb-0">
      <ScrollScanner />
      <Navbar />
      <main id="main-content" className="px-5 pt-10 lg:px-8 lg:pt-16">
        <div className="mx-auto max-w-7xl">
          <Breadcrumbs items={[{ name: "Home", to: "/" }, { name: "Bed Bug Guide" }]} />
        </div>
        <section className="mx-auto max-w-7xl pb-10 pt-10 lg:pt-14">
          <SectionHeader
            kicker="The Bed Bug Guide"
            title="THE BED BUG GUIDE."
            subtitle="Practical, search-focused answers for people dealing with bed bugs across Vancouver and the Lower Mainland."
          />
        </section>
        {loading ? (
          <div className="mx-auto grid max-w-7xl gap-4 pb-24 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="h-44 animate-pulse rounded-lg border border-border bg-muted/20" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <p className="mx-auto max-w-7xl pb-24 text-sm text-muted-foreground">Articles are coming soon.</p>
        ) : (
          <div className="mx-auto grid max-w-7xl gap-4 pb-24 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.04}>
                <Link
                  to={`/guide/${p.slug}`}
                  className="group flex h-full flex-col rounded-lg border border-border bg-muted/20 p-7 transition-colors hover:border-accent/40 hover:bg-muted/40"
                >
                  <span className="font-heading text-xs font-bold uppercase tracking-[0.18em] text-accent">{p.published_date || "Guide"}</span>
                  <h3 className="mt-4 font-heading text-lg font-bold leading-snug text-primary">{p.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{p.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                    Read <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </main>
      <Footer />
      <MobileCTA />
    </div>
  );
}